import React, {Component} from 'react';
import PropTypes from 'prop-types';
import styles from './RegistrationForm.scss';
import moment from 'moment-timezone';

import validateFirstRegisterStep from './validateFirstRegisterStep';
import {Button, ButtonLink, ErrorMessage, Form, FormTextField} from 'components/controls';
import DoctorRegistrationForm from './DoctorRegistrationForm';
import PatientRegistrationForm from './PatientRegistrationForm';
import RegistrationFormStorageHandler from './RegistrationFormStorageHandler';
import DoctorDocumentForm from './DoctorDocumentForm';
import {getCurrentTimezone} from 'sources/timezone/timeHelper';
import normalizeFormToStringValues from 'components/helpers/normalizeFormToStringValues';


class RegistrationForm extends Component {
    static propTypes = {
        onScroll: PropTypes.func.isRequired,
        registration: PropTypes.object,
        registrationProcessing: PropTypes.bool,
        registrationError: PropTypes.object,
        onRegisterUser: PropTypes.func.isRequired,
        onHideRegisterUser: PropTypes.func.isRequired,
        onLoginUserFromRegister: PropTypes.func.isRequired,
        type: PropTypes.string.isRequired,
        step: PropTypes.number.isRequired,
        onLoginLinkClick: PropTypes.func.isRequired,
        onChangeStep: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            avatar: null,
            firstName: '',
            lastName: '',
            middleName: '',
            email: '',
            password: '',
            confirmPassword: '',
            gender: '',
            birthDate: null,
            specialization: '',
            education: '',
            graduation: '',
            certification: '',
            phoneNumber: '',
            faxNumber: '',
            graduationAdditional: '',
            residency: '',
            training: '',
            graduationYear: '',
            npiNumber: '',
            npiNumberValue: '',
            deaNumber: '',
            deaNumberValue: '',
            usePlatformContactInformation: false,
            bio: '',
            languages: [],
            licenses: [],
            timezone: getCurrentTimezone(),
            signature: false,
            acceptPrivacyPolicy: false,
            acceptTerms: false,
            acceptPrescriptionPolicy: false,
            specialties: [],
        };
        RegistrationFormStorageHandler.init();
    }

    componentDidMount() {
        RegistrationFormStorageHandler.getFormData().then(savedState => {
            const {birthDate, ...otherFields} = savedState;
            let newState = {...this.state, ...otherFields};
            if (birthDate)
                newState = Object.assign(newState, {birthDate: moment(birthDate)});
            this.setState({
                ...this.state,
                ...newState
            });
        });
    }

    componentWillReceiveProps(next) {
        const {onChangeStep, registrationError} = this.props;

        if (!registrationError && next.registrationError) {
            onChangeStep(0);
        }

        if (this.isChangedType(next))
            this.refs.form.clearForm();
        if (next.registration && !this.props.registration)
            RegistrationFormStorageHandler.clear();
    }

    isChangedType = (next) => {
        return next.type !== this.props.type;
    }

    componentWillUnmount() {
        if (this.props.registrationError)
            this.props.onHideRegisterUser();
    }

    render() {
        const {step, type} = this.props;

        if (type === 'patient')
            return this.renderPatientForm();

        if (type === 'doctor' && step === 0)
            return this.renderDoctorProfileForm();

        return this.renderDoctorDocumentForm();
    }

    renderDoctorProfileForm() {
        const {onChangeStep, registrationProcessing, registrationError, onScroll} = this.props;

        return (
            <DoctorRegistrationForm
                fieldValues={this.state}
                onFieldChange={this.handleFieldChanged}
                onSubmit={onChangeStep.bind(this, 1)}
                onScroll={onScroll}
                serverError={registrationError}
                processing={registrationProcessing}
                onSaveAndExit={this.handleSaveAndContinueClick}
            />
        );
    }

    renderDoctorDocumentForm() {
        const {registrationProcessing, registrationError, onScroll} = this.props;
        return (
            <DoctorDocumentForm
                fieldValues={this.state}
                onFieldChange={this.handleFieldChanged}
                onSubmit={this.handleRegisterUser}
                onScroll={onScroll}
                serverError={registrationError}
                processing={registrationProcessing}
                onSaveAndExit={this.handleSaveAndContinueClick}
                onChangeState={this.setState.bind(this)}
            />
        );
    }

    renderPatientForm() {
        const {registrationProcessing, registrationError, onScroll} = this.props;
        return (
            <PatientRegistrationForm
                onScroll={onScroll}
                fieldValues={this.state}
                onFieldChange={this.handleFieldChanged}
                onSubmit={this.handleRegisterUser}
                serverError={registrationError}
                processing={registrationProcessing}
            />
        );
    }

    handleNextClick = (step) => {
        const {onChangeStep} = this.props;
        onChangeStep(step);
    }

    handleSaveAndContinueClick = () => {
        const {onHideRegisterUser} = this.props;
        const {birthDate, ...otherFields} = this.state;
        RegistrationFormStorageHandler.saveFormData({
            ...otherFields,
            birthDate: birthDate ? birthDate.valueOf() : null,
        }).then(onHideRegisterUser);
    }

    handleRegisterUser = () => {
        const {onRegisterUser, type} = this.props;
        const {
            avatar, firstName, lastName, middleName, password, bio, phoneNumber, faxNumber, birthDate, email, gender, timezone,
            cv, medicalDiploma, residencyDiploma, pharmacyCertificate, stateMedicalLicences,
            confirmPassword, ...profileFields
        } = this.state;
        const {usePlatformContactInformation} = profileFields;

        const requestBody = normalizeFormToStringValues({
            firstName,
            lastName,
            middleName,
            password,
            bio,
            phoneNumber,
            faxNumber,
            email: email.trim(),
            gender,
            birthDate: birthDate.toDate(),
            timezone: timezone.name,
            role: type
        });

        if (usePlatformContactInformation) {
            delete requestBody.phoneNumber;
            delete requestBody.faxNumber;
        }

        if (type === 'doctor')
            Object.keys(profileFields).forEach(key => {
                if (usePlatformContactInformation &&
                    ['workCity', 'workState', 'workZip', 'workAddress'].includes(key)
                ) {
                    return;
                }

                if (key === 'specialties') {
                    requestBody[`profile[${key}]`] = profileFields[key]
                        .map(({label}) => label)
                        .join(',');
                    return;
                }

                requestBody[`profile[${key}]`] = profileFields[key];
            });

        onRegisterUser(requestBody, {
            cv,
            medicalDiploma,
            residencyDiploma,
            pharmacyCertificate,
            stateMedicalLicences,
            avatar
        });
    }

    handleFieldChanged = (fieldKey, {target: {value}}) => {
        this.setState({
            [fieldKey]: value
        });
    }
}

export default RegistrationForm;



// WEBPACK FOOTER //
// ./src/components/Auth/Register/RegistrationForm.js